import { FC } from "react";

import { UserStateStatsType } from "../../shared/types/monitoring";
import { useAppSelector } from "../../store/index";
import { selectIsClassSessionVirtual } from "../../store/selectors/class-session";
import { selectUserByClassUserId } from "../../store/selectors/class-user";
import {
  selectCameraErrorStatsByCourseId,
  selectHighPingStatsByCourseId,
  selectMicErrorStatsByCourseId,
  selectPoorConnectionStatsByCourseId,
  selectPubsubConnectionStatsByCourseId,
} from "../../store/selectors/monitoring";
import {
  selectOnlineUserCountInCurrentCourse,
  selectUserCountInCurrentCourse,
} from "../../store/selectors/section";
import { useObjectSelector } from "../../store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "../../utils/user-utils";

import {
  AvRefreshStatsTD,
  FailedApiRequestsTD,
  PageLoadStatsTD,
  PageWorksheetLoadErrorTD,
  PoorConnectionWarningTD,
  PreclassCheckFailStatsTD,
  ReloadStreamStatsTD,
  TD,
  TDStatusIconWithTooltip,
  WebsocketDisconnectionsTD,
} from "./Table";

import { Tooltip } from "mds/components/Tooltip";
import { selectCurrentCourseId } from "store/selectors";

const CourseStatsHeader: FC = () => {
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  return (
    <thead>
      <tr>
        <th className="!pl-0">Attendance</th>
        <th>
          <Tooltip text="Number of page loads per participant">Page Loads</Tooltip>
        </th>
        <th>
          <Tooltip text="Number of websocket disconnections per participant">
            Websocket Disconnections
          </Tooltip>
        </th>
        <th>
          <Tooltip text="Number of failed API requests per participant">
            Failed API Requests
          </Tooltip>
        </th>
        <th>
          <Tooltip text="Number of page load errors per participant">Page Load Errors</Tooltip>
        </th>
        <th>
          <Tooltip text="Number of poor connection warnings per participant">
            Poor Connection Warnings
          </Tooltip>
        </th>
        {isVirtualClass && (
          <>
            <th>
              <Tooltip text="Number of AV (excluding av-service-requested and update-service-info) refreshes per participant and percentage of users affected">
                AV Refreshes
              </Tooltip>
            </th>
            <th>
              <Tooltip text="Number of stream reloads per participant and percentage of users affected">
                Stream Reloads
              </Tooltip>
            </th>
          </>
        )}
        <th>
          <Tooltip text="Number of users who are not connected to the Pubsub server">
            Pubsub Connections
          </Tooltip>
        </th>
        <th>
          <Tooltip text="Number of users with high ping round trip time to the Pubsub server">
            Pubsub Ping
          </Tooltip>
        </th>
        {isVirtualClass && (
          <>
            <th>
              <Tooltip text="Number of users with mic errors">Mic Errors</Tooltip>
            </th>
            <th>
              <Tooltip text="Number of users with camera errors">Camera Errors</Tooltip>
            </th>
            <th>
              <Tooltip text="Number of users with poor connection quality to the A/V server">
                A/V Connections
              </Tooltip>
            </th>
            <th>
              <Tooltip text="Number of Test failures per participant and percentage of users affected">
                Pre-class Check Failures
              </Tooltip>
            </th>
          </>
        )}
      </tr>
    </thead>
  );
};

const CourseStatsTooltipContentUser: FC<{
  classUserId: string;
}> = ({ classUserId }) => {
  const user = useObjectSelector(
    (state) => selectUserByClassUserId(state, classUserId),
    PROFILE_PROPERTIES,
  );
  return (
    <div className="text-left">{user ? getUserFullName(user) : `Class user ${classUserId}`}</div>
  );
};

const CourseStatsTooltipContent: FC<{
  userStateStats: UserStateStatsType;
  keyPrefix: string;
}> = ({ userStateStats, keyPrefix }) => {
  return (
    <>
      {userStateStats?.percentAffectedUsers === 0 ? (
        "0% of users affected"
      ) : (
        <>
          <div className="mb-1 text-left font-bold">{`${userStateStats.formattedPercentage} of users affected:`}</div>
          {userStateStats.affectedUsers.map((classUserId) => {
            return (
              <CourseStatsTooltipContentUser
                classUserId={classUserId}
                key={`${keyPrefix}-${classUserId}`}
              />
            );
          })}
        </>
      )}{" "}
    </>
  );
};

const CourseStatsRow: FC<{ courseId: string }> = ({ courseId }) => {
  // TODO: get avService, licodeServer, nActiveUsers and nTotalUsers corresponding to the given
  // courseId instead of the current course.
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);

  const nActiveUsers = useAppSelector(selectOnlineUserCountInCurrentCourse);
  const nTotalUsers = useAppSelector(selectUserCountInCurrentCourse);

  const micErrorStats = useAppSelector((state) => selectMicErrorStatsByCourseId(state, courseId));
  const cameraErrorStats = useAppSelector((state) =>
    selectCameraErrorStatsByCourseId(state, courseId),
  );
  const highPingStats = useAppSelector((state) => selectHighPingStatsByCourseId(state, courseId));
  const poorConnectionStats = useAppSelector((state) =>
    selectPoorConnectionStatsByCourseId(state, courseId),
  );
  const pubsubConnectionStats = useAppSelector((state) =>
    selectPubsubConnectionStatsByCourseId(state, courseId),
  );
  return (
    <tr>
      <TD dataTestId="attendance">{`${nActiveUsers}/${nTotalUsers}`}</TD>
      {/* <TD
          dataTestId="bug-report-count"
          conditionForDanger={bugReportCount >= 1}
        >{`${bugReportCount}`}</TD> */}
      <PageLoadStatsTD courseId={courseId} />
      <WebsocketDisconnectionsTD courseId={courseId} />
      <FailedApiRequestsTD courseId={courseId} />
      <PageWorksheetLoadErrorTD courseId={courseId} />
      <PoorConnectionWarningTD courseId={courseId} />
      {isVirtualClass && (
        <>
          <AvRefreshStatsTD courseId={courseId} />
          <ReloadStreamStatsTD courseId={courseId} />
        </>
      )}
      <TDStatusIconWithTooltip
        conditionForDanger={
          !!pubsubConnectionStats?.percentAffectedUsers &&
          pubsubConnectionStats.percentAffectedUsers >= 50
        }
        conditionForWarning={
          !!pubsubConnectionStats?.percentAffectedUsers &&
          pubsubConnectionStats.percentAffectedUsers >= 20
        }
        dataTestId="pubsub-connection"
      >
        <CourseStatsTooltipContent
          keyPrefix="pubsub-connection"
          userStateStats={pubsubConnectionStats}
        />
      </TDStatusIconWithTooltip>
      <TDStatusIconWithTooltip
        conditionForDanger={
          !!highPingStats?.percentAffectedUsers && highPingStats.percentAffectedUsers >= 50
        }
        conditionForWarning={
          !!highPingStats?.percentAffectedUsers && highPingStats.percentAffectedUsers >= 20
        }
        dataTestId="high-ping"
      >
        <CourseStatsTooltipContent keyPrefix="high-ping" userStateStats={highPingStats} />
      </TDStatusIconWithTooltip>
      {isVirtualClass && (
        <>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!micErrorStats?.percentAffectedUsers && micErrorStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!micErrorStats?.percentAffectedUsers && micErrorStats.percentAffectedUsers >= 20
            }
            dataTestId="mic-error"
          >
            <CourseStatsTooltipContent keyPrefix="mic-error" userStateStats={micErrorStats} />
          </TDStatusIconWithTooltip>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!cameraErrorStats?.percentAffectedUsers &&
              cameraErrorStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!cameraErrorStats?.percentAffectedUsers &&
              cameraErrorStats.percentAffectedUsers >= 20
            }
            dataTestId="camera-error"
          >
            <CourseStatsTooltipContent keyPrefix="camera-error" userStateStats={cameraErrorStats} />
          </TDStatusIconWithTooltip>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!poorConnectionStats?.percentAffectedUsers &&
              poorConnectionStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!poorConnectionStats?.percentAffectedUsers &&
              poorConnectionStats.percentAffectedUsers > 20
            }
            dataTestId="poor-connection"
          >
            <CourseStatsTooltipContent
              keyPrefix="poor-connection"
              userStateStats={poorConnectionStats}
            />
          </TDStatusIconWithTooltip>
          <PreclassCheckFailStatsTD courseId={courseId} />
        </>
      )}
    </tr>
  );
};

export const CourseStats: FC = () => {
  const courseId = useAppSelector(selectCurrentCourseId);
  if (!courseId) return null;
  return (
    <table className="mds-table table-hover" data-testid="course-stats-table">
      <CourseStatsHeader />
      <tbody>
        <CourseStatsRow courseId={courseId} />
      </tbody>
    </table>
  );
};
